import { useReviewsPageProgressContext } from '@/context/reviews-page.context';
import { useStickyProgressCard } from '@/components/reviews-operations-progress/use-sticky-progress-card';
import {
    formatEtaDuration,
    summarizeAutoReplyProgress,
    summarizeExportProgress,
    toPercent,
} from '@/components/reviews-operations-progress/reviews-operations-progress.helpers';
import {
    getAutoReplyProgressCanceledLabel,
    getAutoReplyProgressCardLabels,
    getAutoReplyProgressCardTitle,
    getAutoReplyProgressFailedLabel,
} from '@/shared/auto-replies-labels';

export type ReviewsOperationsProgressCardBinding = {
    kind: 'ai' | 'export' | 'autoReply';
    title: string;
    details: string;
    etaLabel: string;
    isEtaCompleted: boolean;
    progressPercent: number | null;
    tone: 'indigo' | 'amber' | 'emerald';
    onClose: () => void;
};

const formatRetryDateTime = (value: number): string =>
    new Intl.DateTimeFormat('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));

export const useReviewsOperationsProgressController = () => {
    const { aiAnalysisProgress, exportProgress, autoReplyProgress } = useReviewsPageProgressContext();
    const aiProgressCard = useStickyProgressCard(aiAnalysisProgress);
    const exportProgressCard = useStickyProgressCard(exportProgress);
    const autoReplyProgressCard = useStickyProgressCard(autoReplyProgress);
    const visibleAiProgress = aiProgressCard.snapshot;
    const visibleExportProgress = exportProgressCard.snapshot;
    const visibleAutoReplyProgress = autoReplyProgressCard.snapshot;
    const hasAiProgress = aiProgressCard.isVisible && !!visibleAiProgress;
    const hasExportProgress = exportProgressCard.isVisible && !!visibleExportProgress;
    const hasAutoReplyProgress = autoReplyProgressCard.isVisible && !!visibleAutoReplyProgress;

    const cards: ReviewsOperationsProgressCardBinding[] = [];

    if (hasAiProgress && visibleAiProgress) {
        const aiCompleted = !visibleAiProgress.isRunning || aiProgressCard.isGracePeriod;
        const aiRetrying = !!visibleAiProgress.retryMessage && !aiCompleted;

        cards.push({
            kind: 'ai',
            title: 'ИИ-анализ выбранных',
            details:
                visibleAiProgress.retryMessage ??
                `${visibleAiProgress.processed}/${visibleAiProgress.total} • успешно ${visibleAiProgress.successCount} • ошибки ${visibleAiProgress.failedCount}`,
            etaLabel: aiRetrying
                ? visibleAiProgress.retryAt
                    ? `Следующая попытка ${formatRetryDateTime(visibleAiProgress.retryAt)}`
                    : 'Следующая попытка запланирована'
                : aiCompleted
                  ? 'Завершено'
                  : visibleAiProgress.etaSeconds === null
                    ? 'Оценка времени появится после первых результатов'
                    : `Осталось ~${formatEtaDuration(visibleAiProgress.etaSeconds)}`,
            isEtaCompleted: aiCompleted,
            progressPercent: toPercent(visibleAiProgress.processed, visibleAiProgress.total),
            tone: aiRetrying ? 'amber' : 'indigo',
            onClose: aiProgressCard.dismiss,
        });
    }

    if (hasExportProgress && visibleExportProgress) {
        const exportCompleted = !visibleExportProgress.isRunning || exportProgressCard.isGracePeriod;
        const exportSummary = summarizeExportProgress(visibleExportProgress);

        cards.push({
            kind: 'export',
            title: 'Экспорт CSV',
            details: exportSummary.socketUnavailable
                ? 'Ожидаю информацию о прогрессе: нет соединения с socket.io'
                : visibleExportProgress.totalRows !== null
                  ? `${exportSummary.displayedProcessed}/${visibleExportProgress.totalRows} • строк экспортировано`
                  : `Экспортировано ${exportSummary.displayedProcessed} строк`,
            etaLabel: exportCompleted
                ? 'Завершено'
                : exportSummary.socketUnavailable
                  ? 'Ожидаю информацию о прогрессе'
                  : visibleExportProgress.totalRows === null
                    ? visibleExportProgress.etaSeconds === null
                        ? 'Оценка времени недоступна без общего объема'
                        : `Осталось ~${formatEtaDuration(visibleExportProgress.etaSeconds)}`
                    : visibleExportProgress.etaSeconds === null
                      ? 'Оценка времени появится после первых данных'
                      : `Осталось ~${formatEtaDuration(visibleExportProgress.etaSeconds)}`,
            isEtaCompleted: exportCompleted,
            progressPercent: exportSummary.socketUnavailable
                ? null
                : toPercent(exportSummary.displayedProcessed, visibleExportProgress.totalRows),
            tone: 'amber',
            onClose: exportProgressCard.dismiss,
        });
    }

    if (hasAutoReplyProgress && visibleAutoReplyProgress) {
        const autoReplyCompleted = !visibleAutoReplyProgress.isRunning || autoReplyProgressCard.isGracePeriod;
        const autoReplySummary = summarizeAutoReplyProgress(visibleAutoReplyProgress);
        const autoReplyLabels = getAutoReplyProgressCardLabels('ru');

        cards.push({
            kind: 'autoReply',
            title: getAutoReplyProgressCardTitle('ru'),
            details: `${autoReplySummary.displayedProcessed}/${visibleAutoReplyProgress.total} • ${autoReplyLabels.success} ${visibleAutoReplyProgress.successCount} • ${autoReplyLabels.skipped} ${visibleAutoReplyProgress.skippedCount} • ${autoReplyLabels.errors} ${visibleAutoReplyProgress.failedCount}`,
            etaLabel: autoReplySummary.isFailed
                ? getAutoReplyProgressFailedLabel('ru')
                : autoReplySummary.isCanceled
                  ? getAutoReplyProgressCanceledLabel('ru')
                  : autoReplyCompleted
                    ? autoReplyLabels.completed
                    : visibleAutoReplyProgress.etaSeconds === null
                      ? autoReplyLabels.pendingEta
                      : `${autoReplyLabels.remainingPrefix} ~${formatEtaDuration(visibleAutoReplyProgress.etaSeconds)}`,
            isEtaCompleted: autoReplyCompleted,
            progressPercent: toPercent(autoReplySummary.displayedProcessed, visibleAutoReplyProgress.total),
            tone: 'emerald',
            onClose: autoReplyProgressCard.dismiss,
        });
    }

    return {
        cards,
    };
};
